/* tslint:disable:max-line-length */

import {HttpClient, HttpParams} from '@angular/common/http';
import {Injectable} from '@angular/core';
import {Observable} from 'rxjs';
import { APIConfigService } from '../apiconfig.service';

import * as __utils from '../yasag-utils';

import * as __model from '../model';

export interface ListParams {
  /** A search term. */
  search?: string;
  /** Number of results to return per page. */
  limit?: number;
  /** The initial index from which to return the results. */
  offset?: number;
  /** Codi fraccio */
  codi_fraccio?: string;
  /** Fraccio pare */
  fraccio_pare?: string;
  /** Lang */
  lang?: LangListParamsEnum;
  /** Ordenar fracció pel camp ordre */
  ordre?: boolean;
  /** List of fields */
  fields?: string;
  /** List of nested objects */
  expand?: string;
}

export type LangListParamsEnum =
  'ca' |
  'es' |
  'en';

export interface CreateParams {
  data: __model.Fraccio;
  /** List of fields */
  fields?: string;
  /** List of nested objects */
  expand?: string;
}

export interface ExportFractionParams {
  /** A search term. */
  search?: string;
  /** Number of results to return per page. */
  limit?: number;
  /** The initial index from which to return the results. */
  offset?: number;
  /** List of fields */
  fields?: string;
  /** List of nested objects */
  expand?: string;
}

export interface ExportFractionServiceTypeRelationshipParams {
  /** A search term. */
  search?: string;
  /** Number of results to return per page. */
  limit?: number;
  /** The initial index from which to return the results. */
  offset?: number;
  /** List of fields */
  fields?: string;
  /** List of nested objects */
  expand?: string;
}

export interface ImportFractionParams {
  data: __model.ImportCSV;
  /** List of fields */
  fields?: string;
  /** List of nested objects */
  expand?: string;
}

export interface ImportFractionServiceTypeRelationshipParams {
  data: __model.ImportCSV;
  /** List of fields */
  fields?: string;
  /** List of nested objects */
  expand?: string;
}

export interface ReadParams {
  /** List of fields */
  fields?: string;
  /** List of nested objects */
  expand?: string;
  /** A unique value identifying this fraccio. */
  codi_fraccio: string;
}

export interface UpdateParams {
  data: __model.Fraccio;
  /** List of fields */
  fields?: string;
  /** List of nested objects */
  expand?: string;
  /** A unique value identifying this fraccio. */
  codi_fraccio: string;
}

export interface PartialUpdateParams {
  data: __model.Fraccio;
  /** List of fields */
  fields?: string;
  /** List of nested objects */
  expand?: string;
  /** A unique value identifying this fraccio. */
  codi_fraccio: string;
}

export interface DeleteParams {
  /** A unique value identifying this fraccio. */
  codi_fraccio: string;
}

export interface DeleteImageParams {
  /** A unique value identifying this fraccio. */
  codi_fraccio: string;
}

export interface UploadImageParams {
  /** Image file to upload */
  image: File;
  /** List of fields */
  fields?: string;
  /** List of nested objects */
  expand?: string;
  /** A unique value identifying this fraccio. */
  codi_fraccio: string;
}

@Injectable()
export class FraccioService {
  constructor(
    private http: HttpClient,
    private apiConfigService: APIConfigService) {}


  list(params: ListParams, multipart = false): Observable<__model.FraccioList> {
    const queryParamBase = {
      search: params.search,
      limit: params.limit,
      offset: params.offset,
      codi_fraccio: params.codi_fraccio,
      fraccio_pare: params.fraccio_pare,
      lang: params.lang,
      ordre: params.ordre,
      fields: params.fields,
      expand: params.expand,
    };

    let queryParams = __utils.getQueryParams(queryParamBase);

    return this.http.get<__model.FraccioList>(this.apiConfigService.options.apiUrl + `/fraccio/`, {params: queryParams});
  }

  /** Creates an attribute category */
  create(params: CreateParams, multipart = false): Observable<__model.Fraccio> {
    const bodyParams = params.data;
    const bodyParamsWithoutUndefined = __utils.getBodyParamsWithoutUndefined(multipart, bodyParams);

    const queryParamBase = {
      fields: params.fields,
      expand: params.expand,
    };

    let queryParams = __utils.getQueryParams(queryParamBase);

    return this.http.post<__model.Fraccio>(this.apiConfigService.options.apiUrl + `/fraccio/`, bodyParamsWithoutUndefined, {params: queryParams});
  }

  /** Endpoint to export fractions to CSV */
  exportFraction(params: ExportFractionParams, multipart = false): Observable<string> {
    const queryParamBase = {
      search: params.search,
      limit: params.limit,
      offset: params.offset,
      fields: params.fields,
      expand: params.expand,
    };

    let queryParams = __utils.getQueryParams(queryParamBase);

    return this.http.get(this.apiConfigService.options.apiUrl + `/fraccio/export_fraction/`, {params: queryParams, responseType: 'text'});
  }

  /** Endpoint to export fractions-tipus servei relationship to CSV */
  exportFractionServiceTypeRelationship(params: ExportFractionServiceTypeRelationshipParams, multipart = false): Observable<string> {
    const queryParamBase = {
      search: params.search,
      limit: params.limit,
      offset: params.offset,
      fields: params.fields,
      expand: params.expand,
    };

    let queryParams = __utils.getQueryParams(queryParamBase);

    return this.http.get(this.apiConfigService.options.apiUrl + `/fraccio/export_fraction_service_type_relationship/`, {params: queryParams, responseType: 'text'});
  }

  /** Endpoint to import fractions from CSV */
  importFraction(params: ImportFractionParams, multipart = false): Observable<string> {
    const bodyParams = params.data;
    const bodyParamsWithoutUndefined = __utils.getBodyParamsWithoutUndefined(multipart, bodyParams);

    const queryParamBase = {
      fields: params.fields,
      expand: params.expand,
    };

    let queryParams = __utils.getQueryParams(queryParamBase);

    return this.http.post(this.apiConfigService.options.apiUrl + `/fraccio/import_fraction/`, bodyParamsWithoutUndefined, {params: queryParams, responseType: 'text'});
  }

  /** Endpoint to import fractions-tipus servei relationship from CSV */
  importFractionServiceTypeRelationship(params: ImportFractionServiceTypeRelationshipParams, multipart = false): Observable<string> {
    const bodyParams = params.data;
    const bodyParamsWithoutUndefined = __utils.getBodyParamsWithoutUndefined(multipart, bodyParams);

    const queryParamBase = {
      fields: params.fields,
      expand: params.expand,
    };

    let queryParams = __utils.getQueryParams(queryParamBase);

    return this.http.post(this.apiConfigService.options.apiUrl + `/fraccio/import_fraction_service_type_relationship/`, bodyParamsWithoutUndefined, {params: queryParams, responseType: 'text'});
  }

  /** Gets an attribute category with its attributes */
  read(params: ReadParams, multipart = false): Observable<__model.Fraccio> {
    const queryParamBase = {
      fields: params.fields,
      expand: params.expand,
    };

    let queryParams = __utils.getQueryParams(queryParamBase);

    const pathParams = {
      codi_fraccio: params.codi_fraccio,
    };
    return this.http.get<__model.Fraccio>(this.apiConfigService.options.apiUrl + `/fraccio/${pathParams.codi_fraccio}/`, {params: queryParams});
  }

  /** Updates an attribute category */
  update(params: UpdateParams, multipart = false): Observable<__model.Fraccio> {
    const bodyParams = params.data;
    const bodyParamsWithoutUndefined = __utils.getBodyParamsWithoutUndefined(multipart, bodyParams);

    const queryParamBase = {
      fields: params.fields,
      expand: params.expand,
    };

    let queryParams = __utils.getQueryParams(queryParamBase);

    const pathParams = {
      codi_fraccio: params.codi_fraccio,
    };
    return this.http.put<__model.Fraccio>(this.apiConfigService.options.apiUrl + `/fraccio/${pathParams.codi_fraccio}/`, bodyParamsWithoutUndefined, {params: queryParams});
  }

  /** Updates an attribute category partially */
  partialUpdate(params: PartialUpdateParams, multipart = false): Observable<__model.Fraccio> {
    const bodyParams = params.data;
    const bodyParamsWithoutUndefined = __utils.getBodyParamsWithoutUndefined(multipart, bodyParams);

    const queryParamBase = {
      fields: params.fields,
      expand: params.expand,
    };

    let queryParams = __utils.getQueryParams(queryParamBase);

    const pathParams = {
      codi_fraccio: params.codi_fraccio,
    };
    return this.http.patch<__model.Fraccio>(this.apiConfigService.options.apiUrl + `/fraccio/${pathParams.codi_fraccio}/`, bodyParamsWithoutUndefined, {params: queryParams});
  }

  /** Deletes an attribute category from the DB */
  delete(params: DeleteParams, multipart = false): Observable<string> {
    const pathParams = {
      codi_fraccio: params.codi_fraccio,
    };
    return this.http.delete(this.apiConfigService.options.apiUrl + `/fraccio/${pathParams.codi_fraccio}/`, {responseType: 'text'});
  }

  /** Delete the fraction icon image */
  deleteImage(params: DeleteImageParams, multipart = false): Observable<__model.Fraccio> {
    const pathParams = {
      codi_fraccio: params.codi_fraccio,
    };
    return this.http.delete<__model.Fraccio>(this.apiConfigService.options.apiUrl + `/fraccio/${pathParams.codi_fraccio}/delete_image/`);
  }

  /** Upload an image for the fraction icon (icona_frac field) */
  uploadImage(params: UploadImageParams, multipart = false): Observable<__model.Fraccio> {
    const formDataParams = {
      image: params.image,
    };
    const queryParamBase = {
      fields: params.fields,
      expand: params.expand,
    };

    let queryParams = __utils.getQueryParams(queryParamBase);

    const pathParams = {
      codi_fraccio: params.codi_fraccio,
    };
    return this.http.post<__model.Fraccio>(this.apiConfigService.options.apiUrl + `/fraccio/${pathParams.codi_fraccio}/upload_image/`, formDataParams, {params: queryParams});
  }
}
